import {
  BusFrontIcon,
  CookingPotIcon,
  LeafIcon,
  PlaneIcon,
  ShoppingBagIcon,
  TrainIcon,
} from 'lucide-react';
import type React from 'react';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, Source } from 'react-map-gl';

import { Card } from '@/components/ui/card';
import { MapBox } from '@/components/ui/map';
import type { Estate } from '@/features/estates-property/api/estate';
import { cn } from '@/lib/utils';
import { formatDistance } from '@/utils/formatDistance';

import type { EstateAnalysis } from '../api/analytics';
import type { IsochronesData } from '../api/isochrones';
import { useIsochrones } from '../api/isochrones';
import EducationIcon from './icons/education-icon';
import HealthIcon from './icons/health-icon';
import HighwayIcon from './icons/highway-icon';
import { PrintableMap } from './printable-map';
import type { ExportPdfRef } from './types/export-pdf';

type Density = keyof NonNullable<IsochronesData['densities']>;

const ICON_MAPPING: Record<Density, JSX.Element | null> = {
  shopping: <ShoppingBagIcon size={18} color="#C7D422" />,
  leisure: <LeafIcon size={18} color="#BE0707" />,
  health: <HealthIcon />,
  education: <EducationIcon />,
  catering: <CookingPotIcon size={18} color="#07BEA8" />,
};

const COLOR_MAPPING: Record<Density, string | null> = {
  shopping: '#DCEB29',
  leisure: '#BE0707',
  health: '#3BB03D',
  education: '#0784BE',
  catering: '#07BEA8',
};

const COLOR_MAPPING_TIME: Record<string, string> = {
  '15': '#32BE07',
  '30': '#E4DD0A',
  '45': '#BE0707',
};

const TABS = ['car', 'bicycle', 'walk', 'public_transport'] as const;

type Tab = (typeof TABS)[number];

const MAP_ZOOM: Record<Tab, number> = {
  bicycle: 11,
  walk: 12,
  car: 8,
  public_transport: 10,
};

type IsochronesProps = {
  data: Estate;
  estateAnalysis: EstateAnalysis;
};

type OfferPlace = {
  key: string;
  icon: JSX.Element | null;
  densities: number;
};

export const Isochrones = forwardRef<ExportPdfRef, IsochronesProps>(
  ({ data: estate, estateAnalysis }, ref) => {
    const { t } = useTranslation();

    const mapRefs: Record<Tab, React.RefObject<ExportPdfRef>> = {
      car: useRef<ExportPdfRef>(null),
      bicycle: useRef<ExportPdfRef>(null),
      walk: useRef<ExportPdfRef>(null),
      public_transport: useRef<ExportPdfRef>(null),
    };

    const [activeTab, setActiveTab] = useState<Tab>(TABS[0]);

    const { data: isochrones } = useIsochrones(estateAnalysis);

    const tabs = useMemo(() => {
      if (estate.address?.country?.id !== 'AT') {
        return TABS.slice(0, TABS.length - 1);
      } else {
        return TABS;
      }
    }, [estate]);

    const data: Record<string, IsochronesData> | null = useMemo(() => {
      return isochrones[activeTab.toUpperCase()] ?? null;
    }, [isochrones, activeTab]);

    const nearestPlaces = useMemo(() => {
      if (estateAnalysis.pointsOfInterest) {
        const transportPOI = estateAnalysis.pointsOfInterest.data.items.filter(
          (dt) => dt.category === 'transport'
        );
        return [
          {
            labelKey: 'airportName',
            icon: <PlaneIcon size={18} />,
            distance: transportPOI.find((item) => item.subcategory === 'airport')?.distance,
          },
          {
            labelKey: 'highwayAccessName',
            icon: <HighwayIcon />,
            distance: transportPOI.find((item) => item.subcategory === 'highway_ramp')?.distance,
          },
          {
            labelKey: 'busStation',
            icon: <BusFrontIcon size={18} />,
            distance: transportPOI.find((item) => item.subcategory === 'bus')?.distance,
          },
          {
            labelKey: 'trainStation',
            icon: <TrainIcon size={18} />,
            distance: transportPOI.find((item) => item.subcategory === 'train')?.distance,
          },
        ];
      }
      return [];
    }, [estateAnalysis]);

    const offerWithin15Min = useMemo(() => {
      return Object.entries(ICON_MAPPING).reduce<OfferPlace[]>((acc, [key, icon]) => {
        const densities = data?.['15']?.densities?.[key as Density];
        if (densities) {
          acc.push({
            key,
            icon,
            densities,
          });
        }
        return acc;
      }, []);
    }, [data]);

    useImperativeHandle(ref, () => ({
      startExportPdf: async () => {
        const exportPdfProcesses = Object.values(mapRefs).map((el) =>
          el.current?.startExportPdf?.()
        );
        await Promise.all(exportPdfProcesses);
      },
      finishExportPdf: async () => {
        Object.values(mapRefs).map((el) => el.current?.finishExportPdf?.());
      },
    }));

    return (
      <>
        <div className="print:hidden">
          <h2 className="mb-6 text-md font-bold text-primary-dark">{t('isochrones')}</h2>
          <Card>
            <div
              className="grid bg-secondary"
              style={{
                gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))`,
              }}
            >
              {tabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={cn(
                    'relative cursor-pointer select-none',
                    activeTab === tab && 'rounded-tl-md rounded-tr-md bg-white'
                  )}
                  style={{
                    boxShadow: activeTab === tab ? '4px 4px 15px 0px #00000026' : 'none',
                  }}
                  onClick={() => setActiveTab(tab)}
                >
                  <div className="px-11 py-9">
                    <h3 className="text-md font-bold text-primary-dark">{t(tab)}</h3>
                  </div>
                </button>
              ))}
            </div>

            <div className="relative z-10 grid grid-cols-3 bg-white">
              <div className="relative col-span-2 h-[464px]">
                <Card className="absolute left-2 top-[150px] z-10 space-y-2 bg-card-foreground p-4 text-white">
                  <p className="text-sm font-normal italic">{t('travelTime')}</p>
                  {Object.entries(COLOR_MAPPING_TIME).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2">
                      <div className="min-h-6 min-w-6 rounded" style={{ backgroundColor: value }} />
                      <p className="text-base font-bold">
                        {key} {t('minutes')}
                      </p>
                    </div>
                  ))}
                </Card>
                <MapBox
                  key={activeTab}
                  zoom={MAP_ZOOM[activeTab]}
                  latitude={estate.address?.lat}
                  longitude={estate.address?.long}
                >
                  <GeoJonSource data={data} />
                </MapBox>
              </div>
              <div className="p-14">
                <div className="max-w-[250px] space-y-2">
                  {nearestPlaces.map(({ labelKey, distance, icon }) =>
                    distance !== undefined ? (
                      <div key={labelKey} className="flex items-center justify-between gap-4">
                        <div className="flex min-h-6 min-w-6 items-center justify-center rounded bg-primary-darker text-sm text-white">
                          {icon}
                        </div>
                        <p className="flex-1 text-sm font-normal leading-tight text-primary-dark">
                          {t(labelKey)}
                        </p>
                        <p className="text-sm font-normal italic leading-tight text-primary-dark">
                          {formatDistance(distance)}
                        </p>
                      </div>
                    ) : null
                  )}
                </div>
                <p className="mb-4 mt-6 text-base font-bold leading-tight text-primary-dark">
                  {t('offersWithinXMin', { minutes: 15 })}
                </p>
                {offerWithin15Min.length === 0 ? (
                  <p className="text-sm text-text-secondary">
                    {t('noOffersWithinXMin', { minutes: 15 })}
                  </p>
                ) : (
                  <div className="max-w-[250px] space-y-2">
                    {offerWithin15Min.map(({ key, densities, icon }) => (
                      <div key={key} className="flex items-center justify-between gap-4">
                        <div
                          className="flex min-h-6 min-w-6 items-center justify-center rounded text-sm text-white"
                          style={{ backgroundColor: `${COLOR_MAPPING[key as Density]}25` }}
                        >
                          {icon}
                        </div>
                        <p className="flex-1 text-sm font-normal text-primary-dark">{t(key)}</p>
                        <p className="text-sm font-normal italic text-primary-dark">{densities}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
        <div className="print:space-y-10">
          {tabs.map((tab) => {
            return (
              <div key={`PRINT-${tab}`} className="mt-4 flex gap-8">
                <h3 className="hidden min-w-40 text-[14px] font-bold print:block">{t(tab)}</h3>
                <IsochroneMap
                  ref={mapRefs[tab]}
                  estate={estate}
                  id={tab}
                  data={isochrones[tab.toUpperCase()]}
                />
              </div>
            );
          })}
        </div>
      </>
    );
  }
);

const GeoJonSource = ({ data }: { data?: Record<string, IsochronesData> }) => {
  if (data) {
    return Object.entries(data).map(([key, value]) => {
      if (value.isochrone?.geometry) {
        return (
          <Source
            key={`polygon_${key}`}
            id={`polygon_${key}`}
            type="geojson"
            data={value.isochrone?.geometry}
          >
            <Layer
              id={`polygon_fill_${key}`}
              type="fill"
              paint={{
                'fill-color': COLOR_MAPPING_TIME[key],
                'fill-opacity': 0.2,
              }}
            />
            <Layer
              id={`polygon_stroke_${key}`}
              type="line"
              paint={{
                'line-color': COLOR_MAPPING_TIME[key],
                'line-width': 3,
                'line-opacity': 1,
              }}
            />
          </Source>
        );
      }
      return null;
    });
  }
};

const IsochroneMap = forwardRef<
  ExportPdfRef,
  { id: Tab; estate: Estate; data?: Record<string, IsochronesData> }
>(({ id, estate, data }, ref) => {
  const { t } = useTranslation();

  return (
    <div className="relative col-span-2 flex-1">
      <Card className="absolute left-2 top-2 z-10 hidden space-y-2 rounded-sm bg-card-foreground p-2 text-white print:block">
        <p className="text-sm font-normal italic">{t('travelTime')}</p>
        {Object.entries(COLOR_MAPPING_TIME).map(([key, value]) => (
          <div key={key} className="flex items-center gap-2">
            <div
              className="min-h-6 min-w-6 rounded print:min-h-3 print:min-w-3"
              style={{ backgroundColor: value }}
            />
            <p className="text-[12px] font-semibold print:text-xs">
              {key} {t('minutes')}
            </p>
          </div>
        ))}
      </Card>

      <PrintableMap
        ref={ref}
        key={id}
        latitude={estate.address?.lat}
        longitude={estate.address?.long}
        zoom={MAP_ZOOM[id] - 1}
        fullscreenControl={false}
        navigationControl={false}
        defaultDisplay="none"
        className="relative h-[200px] w-full rounded-md object-cover"
      >
        <GeoJonSource data={data} />
      </PrintableMap>
    </div>
  );
});
